import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";

import { BehaviorSubject, filter, from, map, take } from "rxjs";
import { FormControlOptionsUtils } from "../control-options/utils/form-control-options-utils";
import { FormControlService } from "../form-control.service";
import { FormControlOptionsMap, IControl, IDateMinRule, IPatternRule, IStringLengthRule, LControlType, LValidationRule, ValidationRule, ValidationRuleMap } from "../types";
import { SSFormGroup } from "./ss-form-group";



const validatorTransform: { [KValidationRule in LValidationRule]?: (validatonRule: ValidationRuleMap[KValidationRule]) => ValidatorFn } = {

  pattern: (rule: IPatternRule) => {

    const customPatternValidatorFn: ValidatorFn = (control) => {

      const patternValidatorFn = Validators.pattern(rule.pattern);

      const patternValidatonResult = patternValidatorFn(control);
      if (!rule.key || !patternValidatonResult) {
        return patternValidatonResult
      }

      return { [rule.key]: true }
    }

    return customPatternValidatorFn
  },

  required: () => (ctrl: AbstractControl) => {

    if (ctrl.value instanceof Array) {
      if (ctrl.value.length === 0) {

        return { required: true }
      }
    }
    return Validators.required(ctrl)
  },

  stringLength: (rule: IStringLengthRule) => {

    const stringLengthValidatorFn: ValidatorFn = (control: AbstractControl) => {

      const strLenMinValidatorFn = rule.min && Validators.minLength(rule.min)
      const strLenMaxValidatorFn = rule.max && Validators.maxLength(rule.max)

      const validationMinResult = strLenMinValidatorFn && strLenMinValidatorFn(control);
      const validationMaxResult = strLenMaxValidatorFn && strLenMaxValidatorFn(control);

      if (!validationMaxResult && !validationMinResult) {
        return null;
      }

      return {
        stringLength: true
      }
    }

    return stringLengthValidatorFn;
  },
  email: () => (control) => {
    const validationResult = Validators.email(control);

    if (validationResult !== null) {
      return validationResult;
    }

    const [, afterAt] = (control.value || '' as string).split('@', 2);

    const [, afterDot] = (afterAt || '').split('.', 2);
    if (afterDot?.length >= 2) {
      return null;
    }

    return { email: true }

  },
  dateMin: (rule: IDateMinRule) => (ctrl) => {
    try {

      if (!ctrl.value) {
        return null
      }

      const date = new Date(ctrl.value)
      if (date.getTime() < rule.min.getTime()) {
        return { 'dateMin': true }
      }
    }
    catch {
      return null
    }



  }
}


const asnycValidatorTransform: { [KValidationRule in LValidationRule]?: (validatonRule: ValidationRuleMap[KValidationRule], formGroup: SSFormGroup) => AsyncValidatorFn } = {
  async: (rule, formGroup: SSFormGroup): AsyncValidatorFn => {

    const asyncValidatorFn: AsyncValidatorFn = (control: AbstractControl) => {

      const value = control.value;


      const ruleKey = rule.key || rule.type;
      const validity = rule.validationCallback(value, formGroup);

      const resultMapper = (result: boolean, key: string): ValidationErrors | null => {


        if (result === true) {
          return null;
        }

        return {
          [key]: true
        }
      }

      if (validity instanceof Promise) {
        return validity.then((result) => resultMapper(result, ruleKey))
      }

      return validity.pipe(map((result) => resultMapper(result, ruleKey))).pipe(take(1))
    }

    return asyncValidatorFn;
  },
  splug: (rule) => {

    const asyncValidatorFn: AsyncValidatorFn = (control: AbstractControl) => {

      const value = control.value;

      if (!value) {
        return new Promise((resolve) => resolve(null));
      }

      const ruleKey = rule.key || rule.type;

      //todo
      // const validity = mockApi.validateSPLUG(value);

      const resultMapper = (result: boolean, key: string): ValidationErrors | null => {
        if (result === true) {
          return null;
        }

        return {
          [key]: true
        }
      }

      return from(Promise.resolve(true)).pipe(map((valid => resultMapper(valid, ruleKey))))
    }

    return asyncValidatorFn;
  }

}


export class SSFormControl<
  TControlType extends LControlType = LControlType,
  _ControlOptions extends FormControlOptionsMap[LControlType] = FormControlOptionsMap[TControlType]
> extends FormControl {

  private _label: string = ''
  get label() {
    return this._label;
  }
  set label(value: string) {
    this._label = value;
  }

  private _formGroup: SSFormGroup

  get formGroup() {
    return this._formGroup
  }

  set formGroup(value: SSFormGroup) {
    this._formGroup = value
    this._initValidators();
  }


  private _readOnly?: boolean
  get readOnly() {
    return this._readOnly
  }
  set readOnly(value) {
    this._readOnly = value;
  }

  private controlServiceSubject = new BehaviorSubject<FormControlService>(undefined)
  get controlService() {
    return this.controlServiceSubject.value;
  }
  set controlService(value) {
    this.controlServiceSubject.next(value);
  }

  readonly options = FormControlOptionsUtils.instantiateOptions<TControlType>(this._control)
  readonly touchedSubject = new BehaviorSubject<boolean>(false);
  readonly actionSubject = new BehaviorSubject<IControl['action']>(null)

  validationRules?: ValidationRule[]

  get initialValue() {
    return this._control.defaultValue;
  }
  constructor(
    private _control: IControl<TControlType>
  ) {



    super(_control.defaultValue || null, { nonNullable: Boolean(_control.defaultValue) });

    this.actionSubject.next(_control.action);
    this.label = this._control.label;
    this.readOnly = _control.readOnly;
  }


  private _initValidators() {
    this.validationRules = this._control.validationRules;

    const asyncValidators = this._getAsyncValidators()
    this.setAsyncValidators(asyncValidators);
    this.setValidators(this._getValidators());
    this.updateValueAndValidity();

  }

  private _getAsyncValidators(control: IControl = this._control): AsyncValidatorFn[] | null {

    const validationRules = control.validationRules

    if (!validationRules?.length) {
      return null;
    }

    const retAsyncValidators: AsyncValidatorFn[] = validationRules
      .filter((validationRule) => Boolean(asnycValidatorTransform[validationRule.type]))
      .map((validationRule) => {

        const transformFunction = asnycValidatorTransform[validationRule.type] as Function;

        return transformFunction(validationRule, this.formGroup)

      })

    return retAsyncValidators;
  }

  get isLast() {
    return (this as any).isLastSubject.value;
  }
  private _getValidators(control: IControl = this._control): ValidatorFn[] | null {

    const validationRules = control.validationRules
    if (!validationRules?.length) {
      return null;
    }

    const retValidators: ValidatorFn[] = validationRules
      .filter(({ type }) => Boolean(validatorTransform[type]))
      .map((validationRule) => {

        const transformFunction = validatorTransform[validationRule.type] as Function;

        return transformFunction(validationRule)
      })

    return retValidators;

  }

  getBrokenRules(): ValidationRule[] {

    const validatonRules = this._control.validationRules;


    if (!this.errors || !validatonRules?.length) {
      return []
    }
    const errorKeys = Object.keys(this.errors);

    const validationResult = errorKeys
      .map((errorKey) => {

        const validatonRuleByType = validatonRules.find(({ type }) => type === errorKey);
        const validatonRuleByKey = validatonRules
          .filter((rule) => rule.key)
          .find((rule) => rule.key === errorKey);

        if (validatonRuleByType) {
          return validatonRuleByType
        }

        if (validatonRuleByKey) {
          return validatonRuleByKey;
        }

        return null
      })


    const brokenRules = <ValidationRule[]>validationResult.filter(Boolean);

    return brokenRules;

  }


  focus() {
    this.controlServiceSubject.pipe(filter(Boolean), take(1)).subscribe((controlService) => {
      controlService.focus()
    });

  }

  dispose() {
    (this.options as any).dispose();
  }

}
